/**
 * Simulator Guide Controller (EPIC-068.5)
 *
 * Visite guidée du simulateur prévisionnel : surcouche non bloquante qui met
 * en avant les fieldsets de la colonne gauche un par un (spotlight), affiche un
 * panneau d'aide contextuel sous le fieldset actif et propose une navigation
 * Précédent / Passer / Suivant.
 *
 * Mécanique (cf. décisions D1→D6 du sous-EPIC) :
 *  - D1 : panneau unique déplacé dans le flux DOM via `fieldset.after(panel)`
 *         (pas de popover ancré, pas de recalcul au scroll).
 *  - D2 : 6 étapes → 4 fieldsets (étapes 1 et 2 ciblent toutes deux
 *         `activities`, l'étape 6 retire tout spotlight et affiche la conclusion).
 *  - D3 : toute fermeture anticipée (✕ ou Échap) pose le flag localStorage
 *         `nm_simulator_guided_done` → pas de réouverture automatique ; seul le
 *         bouton « Visite guidée » force la réouverture.
 *  - D4 : spotlight non bloquant — `nm-guide-dimmed` réduit l'opacité sans
 *         couper `pointer-events`, les résultats live restent visibles.
 *  - D6 : ARIA — région `aria-live` annonçant « Étape N sur 6 : <titre> »,
 *         focus déplacé sur le titre du panneau, « Précédent » désactivé à
 *         l'étape 1.
 *
 * Les 6 textes d'aide sont dans des `<template id="tpl-guide-step-N">` du HTML
 * (pas de HTML construit dans le JS).
 */

import BaseController from "./base_controller.js";

const STORAGE_KEY = "nm_simulator_guided_done";

// Étape (0-indexée) → fieldset cible (data-guide-section). `null` = étape finale
// sans spotlight (conclusion).
const STEP_SECTIONS = ["activities", "activities", "aides", "tax", "charges", null];

export default class SimulatorGuideController extends BaseController {
  static targets = [
    "panel",
    "counter",
    "title",
    "body",
    "prevBtn",
    "skipBtn",
    "nextBtn",
    "live",
  ];

  connect() {
    this.current = 0;
    this.open = false;
    this.onKeydown = this.handleKeydown.bind(this);

    this.sectionEls = Array.from(
      this.element.querySelectorAll("[data-guide-section]"),
    );
    this.formColumnEl = this.element.querySelector(".nm-sim-form");

    // Ouverture automatique au tout premier passage (flag absent).
    if (!this.isDone()) {
      this.start();
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown);
  }

  // ---------------------------------------------------------------------------
  // localStorage flag
  // ---------------------------------------------------------------------------

  isDone() {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }

  markDone() {
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      /* localStorage indisponible (mode privé strict) — non bloquant */
    }
  }

  // ---------------------------------------------------------------------------
  // Cycle de vie de la visite
  // ---------------------------------------------------------------------------

  /** Démarre / rejoue la visite à l'étape 1 (ignore le flag localStorage). */
  start() {
    this.open = true;
    this.panelTarget.hidden = false;
    document.addEventListener("keydown", this.onKeydown);
    this.goTo(0);
  }

  /** Ferme la visite et pose le flag « vu » (D3). */
  finish() {
    this.open = false;
    this.panelTarget.hidden = true;
    this.clearHighlight();
    document.removeEventListener("keydown", this.onKeydown);
    this.markDone();
  }

  next() {
    if (this.current >= STEP_SECTIONS.length - 1) {
      this.finish();
      return;
    }
    this.goTo(this.current + 1);
  }

  prev() {
    if (this.current > 0) {
      this.goTo(this.current - 1);
    }
  }

  /** « Passer cette étape » — avance sans fermer (jamais sur la dernière). */
  skip() {
    this.goTo(this.current + 1);
  }

  // ---------------------------------------------------------------------------
  // Rendu d'une étape
  // ---------------------------------------------------------------------------

  goTo(index) {
    this.current = Math.max(0, Math.min(index, STEP_SECTIONS.length - 1));
    const isLast = this.current === STEP_SECTIONS.length - 1;
    const section = STEP_SECTIONS[this.current];

    this.renderContent(this.current);
    this.applyHighlight(section);
    this.placePanel(section);
    this.updateNav(isLast);

    const total = STEP_SECTIONS.length;
    const stepLabel = `Étape ${this.current + 1} sur ${total} : ${this.titleTarget.textContent}`;
    this.liveTarget.textContent = stepLabel;

    // D6 — déplacer le focus sur le titre du panneau au changement d'étape.
    this.titleTarget.focus();
  }

  renderContent(index) {
    const frag = this.cloneTemplate(`tpl-guide-step-${index + 1}`);
    const wrap = frag.firstElementChild;
    if (!wrap) return;
    this.titleTarget.textContent = wrap.dataset.guideTitle || "";
    this.bodyTarget.replaceChildren(...wrap.childNodes);
    this.counterTarget.textContent = `Étape ${index + 1} / ${STEP_SECTIONS.length}`;
  }

  updateNav(isLast) {
    this.prevBtnTarget.disabled = this.current === 0;
    this.skipBtnTarget.hidden = isLast;
    this.nextBtnTarget.textContent = isLast ? "Terminer la visite" : "Suivant";
  }

  // ---------------------------------------------------------------------------
  // Spotlight (D4) + placement du panneau (D1)
  // ---------------------------------------------------------------------------

  applyHighlight(section) {
    this.clearHighlight();
    if (!section) return; // étape finale — pas de spotlight
    for (const el of this.sectionEls) {
      if (el.dataset.guideSection === section) {
        el.classList.add("nm-guide-active");
      } else {
        el.classList.add("nm-guide-dimmed");
      }
    }
  }

  clearHighlight() {
    for (const el of this.sectionEls) {
      el.classList.remove("nm-guide-active", "nm-guide-dimmed");
    }
  }

  placePanel(section) {
    const target = section
      ? this.sectionEls.find((el) => el.dataset.guideSection === section)
      : null;
    if (target) {
      target.after(this.panelTarget);
    } else if (this.formColumnEl) {
      this.formColumnEl.append(this.panelTarget);
    }
  }

  // ---------------------------------------------------------------------------
  // Clavier (Échap = fermeture anticipée, D3)
  // ---------------------------------------------------------------------------

  handleKeydown(event) {
    if (this.open && event.key === "Escape") {
      event.preventDefault();
      this.finish();
    }
  }
}
